import { Play } from '@tunasong/icons'
import type { AugDimWarmupPair, AugDimWarmupVerdict } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './aug-dim-warmup-feedback.module.css'

export interface AugDimWarmupFeedbackProps {
  pair: AugDimWarmupPair
  verdict: AugDimWarmupVerdict
  onHearAgain: () => void
  onNext: () => void
}

/**
 * Aug/Dim Warm-up post-answer reveal. Verdict header + reveal sentence
 * ("The {augmented|diminished} chord was {first|second}.") + lexicon body
 * from `ear-training-warmup-feedback`. Hear-it-again + Next actions.
 *
 * Spec: docs/superpowers/specs/2026-05-21-ear-training-v15-design.md §5.3
 */
export const AugDimWarmupFeedback: FC<AugDimWarmupFeedbackProps> = props => {
  const { verdict, onHearAgain, onNext } = props
  const lexicon = useTheoryCopy('ear-training-warmup-feedback')
  const header = verdict.correct ? 'Correct.' : 'Not quite.'
  const lexBody = lexicon?.body ?? ''
  return (
    <section className={styles.feedback} role="status" aria-live="polite">
      <p className={`${styles.header} ${verdict.correct ? styles.correct : styles.incorrect}`}>{header}</p>
      <p className={styles.body}>
        The {verdict.targetKind} chord was {verdict.targetPosition}.
        {verdict.correct ? null : <> You picked {verdict.picked}.</>}
      </p>
      {lexBody ? (
        <p className={styles.body}>
          <TheoryGlyphs>{lexBody}</TheoryGlyphs>
        </p>
      ) : null}
      <div className={styles.actions}>
        <Button variant="text" size="small" startIcon={<Play />} onClick={onHearAgain}>
          Hear it again
        </Button>
        <Button variant="contained" color="primary" onClick={onNext}>
          Next
        </Button>
      </div>
    </section>
  )
}
